/**
 * ChecklistTypesDialog - Cadastro de Tipos de Checklist.
 *
 * Aberto a partir de Configurações › Template de Checklist (botão "Tipos").
 * Permite criar, renomear e ativar/inativar tipos. Tipos inativos deixam de
 * aparecer nas novas seleções, mas seguem vinculados aos templates antigos.
 *
 * @version 1.1.0
 * RELEASE: 2026-09-26 v1.1.0 - Ação textual padronizada no tamanho compacto de Departamentos.
 * RELEASE: 2026-09-26 v1.0.0 - Criação inicial.
 */
import { useState } from 'react';
import { Check, Pencil, Plus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useChecklistTypes, useUpsertChecklistType, type ChecklistType } from '@/hooks/useChecklistTypes';

export function ChecklistTypesDialog({ open, onOpenChange, canEdit }: {
  open: boolean; onOpenChange: (o: boolean) => void; canEdit: boolean;
}) {
  const { data: types = [] } = useChecklistTypes(true, open);
  const upsert = useUpsertChecklistType();
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);

  const save = (t: ChecklistType, patch: Partial<ChecklistType>) =>
    upsert.mutate({ id: t.id, data: { name: t.name, description: t.description, color: t.color, is_active: t.is_active, sort_order: t.sort_order, ...patch } });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Tipos de Checklist</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {types.map((t) => (
            <div key={t.id} className="flex items-center gap-2 rounded-md border px-3 py-2">
              {editing?.id === t.id ? (
                <>
                  <Input autoFocus maxLength={80} className="h-8" value={editing.name}
                    onChange={(e) => setEditing({ id: t.id, name: e.target.value })} />
                  <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Salvar"
                    disabled={!editing.name.trim()} onClick={() => { save(t, { name: editing.name.trim() }); setEditing(null); }}><Check className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Cancelar" onClick={() => setEditing(null)}><X className="h-4 w-4" /></Button>
                </>
              ) : (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.templates_count} template(s)</div>
                  </div>
                  {canEdit && (
                    <>
                      <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Renomear" onClick={() => setEditing({ id: t.id, name: t.name })}><Pencil className="h-4 w-4" /></Button>
                      <Switch checked={t.is_active} onCheckedChange={(v) => save(t, { is_active: v })} aria-label="Ativo" />
                    </>
                  )}
                </>
              )}
            </div>
          ))}
          {canEdit && (
            <div className="flex gap-2 pt-2">
              <Input maxLength={80} placeholder="Novo tipo (ex.: Entrega, Devolução)" value={newName}
                onChange={(e) => setNewName(e.target.value)} />
              <Button size="xs" disabled={!newName.trim() || upsert.isPending}
                onClick={() => { upsert.mutate({ data: { name: newName.trim() } }); setNewName(''); }}>
                <Plus className="mr-1 h-4 w-4" />Adicionar
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Seletor de tipo reutilizado no editor, na criação de template e nas vistorias. */
export function ChecklistTypeSelect({ value, onChange, disabled, keepId }: {
  value: string; onChange: (v: string) => void; disabled?: boolean; keepId?: string | null;
}) {
  const { data: types = [] } = useChecklistTypes(true);
  // Mostra somente ativos, preservando o tipo atual mesmo se inativo.
  const visible = types.filter((t) => t.is_active || t.id === keepId);
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
      <SelectContent>
        {visible.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{!t.is_active && ' (inativo)'}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
